import React from 'react';
import PropTypes from 'prop-types';
import { Dimensions } from 'react-native';
import { Input as NBInput, TextArea as NBTextArea } from 'native-base';
import { Icon } from '~/native-ui/Icon';
import { FormControl } from '~/native-ui/FormControl';

const factory = (FontIcon) => {
    class Input extends React.Component {
        static propTypes = {
            variant: PropTypes.string,
            children: PropTypes.node,
            defaultValue: PropTypes.string,
            disabled: PropTypes.bool,
            invalid: PropTypes.bool,
            error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            iconLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            iconRight: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            maxLength: PropTypes.number,
            multiline: PropTypes.bool,
            name: PropTypes.string,
            onBlur: PropTypes.func,
            onChangeText: PropTypes.func,
            onChange: PropTypes.func,
            onFocus: PropTypes.func,
            onKeyPress: PropTypes.func,
            required: PropTypes.bool,
            rows: PropTypes.number,
            type: PropTypes.string,
            iconSize: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
        };

        static defaultProps = {
            placeholder: '',
            invalid: false,
            disabled: false,
            multiline: false,
            required: false,
            type: 'text',
            iconSize: 'xs',
            onChange: (event) => {},
            onChangeText: (value, event) => {},
        };

        addAutoResize() {
            Dimensions.addEventListener('change', this.handleAutoresize);
        }

        removeAutoResize() {
            Dimensions.removeEventListener('change', this.handleAutoresize);
        }

        componentDidMount() {
            if (this.props.multiline) {
                this.addAutoResize();
                this.handleAutoresize();
            }
        }

        componentWillReceiveProps(nextProps) {
            if (!this.props.multiline && nextProps.multiline) {
                this.addAutoResize();
            } else if (this.props.multiline && !nextProps.multiline) {
                this.removeAutoResize();
            }
        }

        componentDidUpdate() {
            // resize the textarea, if nessesary
            if (this.props.multiline) {
                this.handleAutoresize();
            }
        }

        componentWillUnmount() {
            if (this.props.multiline) {
                this.removeAutoResize();
            }
        }

        handleChange = (event) => {
            const { multiline, maxLength } = this.props;
            const valueFromEvent = event.target.value;

            // Trim value to maxLength if that exists (only on multiline inputs).
            // Note that this is still required even tho we have the onKeyPress filter
            // because the user could paste smt in the textarea.
            const haveToTrim = multiline && maxLength && event.target.value.length > maxLength;
            const value = haveToTrim ? valueFromEvent.substr(0, maxLength) : valueFromEvent;

            // propagate to to store and therefore to the input
            this.props.onChange(value, event);
            // this.props.onChangeText(value, event);
        };

        handleAutoresize = () => {
            if (!this.inputNode) {
                return;
            }

            const element = this.inputNode;
            const rows = this.props.rows;

            if (typeof rows === 'number' && !isNaN(rows)) {
                element.style.height = null;
            } else {
                // compute the height difference between inner height and outer height
                const style = getComputedStyle(element, null);
                const heightOffset =
                    style.boxSizing === 'content-box'
                        ? -(parseFloat(style.paddingTop) + parseFloat(style.paddingBottom))
                        : parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);

                // resize the input to its content size
                element.style.height = 'auto';
                element.style.height = `${element.scrollHeight + heightOffset}px`;
            }
        };

        handleKeyPress = (event) => {
            // prevent insertion of more characters if we're a multiline input
            // and maxLength exists
            const { multiline, maxLength, onKeyPress } = this.props;
            if (multiline && maxLength) {
                // check if smt is selected, in which case the newly added charcter would
                // replace the selected characters, so the length of value doesn't actually
                // increase.
                const isReplacing = event.target.selectionEnd - event.target.selectionStart;
                const value = event.target.value;

                if (!isReplacing && value.length === maxLength) {
                    event.preventDefault();
                    event.stopPropagation();
                    return undefined;
                }
            }

            if (onKeyPress) onKeyPress(event);
            return undefined;
        };

        blur() {
            this.inputNode.blur();
        }

        focus() {
            this.inputNode.focus();
        }

        valuePresent = (value) =>
            value !== null && value !== undefined && value !== '' && !(typeof value === 'number' && isNaN(value));

        setRef = (ref) => {
            this.inputNode = ref;
            if (!this.props.forwardRef) return;
            typeof this.props.forwardRef === 'function'
                ? this.props.forwardRef?.(ref)
                : (this.props.forwardRef.current = ref);
        };

        errorStyle = { color: 'danger.900' };
        render() {
            const {
                forwardRef,
                children,
                defaultValue,
                disabled,
                error,
                floating,
                placeholder,
                iconLeft,
                iconRight,
                name,
                label: labelText,
                tooltip,
                maxLength,
                multiline,
                required,
                role,
                iconSize,
                variant,
                childrenBeforeInput = false,
                theme,
                type,
                onKeyPress,
                rows = 1,
                value,
                invalid,
                size,
                ...others
            } = this.props;
            const length = maxLength && value ? value.length : 0;

            const inputElementProps = {
                fontSize: '14',
                ...others,
                role,
                name,
                defaultValue,
                type,
                value,
                maxLength,
                placeholder: placeholder,
                variant: this.props.isReadOnly ? 'readOnly' : variant,
                InputLeftElement: iconLeft ? (
                    <FontIcon value={iconLeft} color="muted.400" size={iconSize} />
                ) : undefined,
                InputRightElement: iconRight ? (
                    <FontIcon value={iconRight} color="muted.400" size={iconSize} />
                ) : undefined,
                ref: this.setRef,
                onChange: this.handleChange,
            };

            if (!multiline) {
                inputElementProps.onKeyPress = onKeyPress;
            } else {
                inputElementProps.totalLines = rows;
                inputElementProps.onKeyPress = this.handleKeyPress;
            }

            return (
                <FormControl
                    mt={!this.props.isReadOnly ? 'auto' : undefined}
                    isRequired={required}
                    isDisabled={disabled}
                    isInvalid={invalid}
                >
                    {!error && labelText && <FormControl.Label label={labelText} tooltip={tooltip} />}
                    {error && <FormControl.Label label={error} {...this.errorStyle} tooltip={tooltip} />}
                    {childrenBeforeInput ? children : null}
                    {React.createElement(multiline ? NBTextArea : NBInput, inputElementProps)}

                    {/**  TODO: Review Hint and max length */}
                    {/* {hint ? <span hidden={labelText} className={theme.hint}>{hint}</span> : null} */}
                    {/* {maxLength ? <MaxLengthCounter color="muted.400">{length}/{maxLength}</MaxLengthCounter> : null} */}
                    {!childrenBeforeInput ? children : null}
                </FormControl>
            );
        }
    }

    return React.memo(
        React.forwardRef((props, ref) => {
            return <Input {...props} forwardRef={ref} />;
        })
    );
};

const Input = factory(Icon);
export { factory as inputFactory };
export { Input };

// const MaxLengthCounter = styled(Text)`
//   position: absolute;
//   right: 0;
//   bottom: 100%;
// `
